import React from 'react';
import { Container } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import Elevio from 'elevio/lib/react';

import { AgencyPrivacyPolicyPage, AgencyTermsOfUsePage } from '@containers/common/AgencyLegal';
import AgencyUserSetup from '@containers/common/AgencyUserSetup';
import Alert from '@containers/common/Alert';
import CRMApp from '@containers/crm/CRMApp';
import PaymentPortalApp from '@containers/payments/PaymentPortalApp';

import PrivateRoute from '@components/PrivateRoute';

import PrivateSubmissionApp from '@private-crm-app/PrivateSubmissionApp/PrivateSubmissionApp';

import { loginSuccess } from '@actions/auth';

import { getUserProfile, isLoggedIn, oauthLoginCallbackURL, oauthLogoutCallbackURL } from '@utils/AuthUtil';

import StaffTextShareLocation from '../StaffTextShareLocation/StaffTextShareLocation';

import Home from './Home';
import LoginCallback from './LoginCallback';
import LogoutCallback from './LogoutCallback';

import './style.scss';
// import Loading from '@components/Loading';

interface IAppProps extends ICommonProps {
  loginSuccess: TLoginSuccess;
  productLayout: IProductLayoutResponse;
  isFetchingProductLayout?: boolean;
}

interface IAppState {
  accountId: string;
  isAccountIdFetched: boolean;
}

const PrivateCSApp = loadable(
  () =>
    import(
      /* webpackChunkName: "private-cs" */
      '@containers/crm/PrivateCSApp'
    )
);

const PrivateCEApp = loadable(
  () =>
    import(
      /* webpackChunkName: "private-ce" */
      '@containers/cases/PrivateCEApp'
    )
);

class App extends React.Component<IAppProps, IAppState> {
  elevioRef = React.createRef<Elevio>();

  constructor(props: IAppProps) {
    super(props);
    this.state = this.getEmptyState();
  }

  async componentWillMount() {
    if (!!window?.localStorage && isLoggedIn()) {
      const UserProfile: IUserProfile = getUserProfile();
      await this.props.loginSuccess(UserProfile);
    }
  }

  componentDidUpdate(prevProps: IAppProps, prevState: IAppState) {
    const hasProductLayoutChanged = this.props.productLayout !== prevProps.productLayout && !!this.props.productLayout;
    if (hasProductLayoutChanged && !this.state.isAccountIdFetched) {
      this.setState({
        accountId:
          (this.props.productLayout.agency.agencyInfo && this.props.productLayout.agency.agencyInfo.elevioAccountId) ||
          process.env.ELEVIO_ID,
        isAccountIdFetched: true,
      });
    }
  }

  getEmptyState = (): IAppState => ({ accountId: null, isAccountIdFetched: false });

  render() {
    return (
      <Container fluid={true}>
        <div role="region" aria-label="Help and support">
          {!window.location.search.includes('translate') && this.state.isAccountIdFetched && (
            <Elevio key={this.state.accountId} accountId={this.state.accountId} />
          )}
        </div>
        <Switch>
          <Route exact path="/agencies/:agencyId/login" component={AgencyUserSetup} />
          <Route exact path="/agencies/:agencyId/error" component={AgencyUserSetup} />
          <Route exact path="/text-location/:token" component={StaffTextShareLocation} />
          <Route exact path="/agencies/:crmToken/privacy-policy" component={AgencyPrivacyPolicyPage} />
          <Route exact path="/agencies/:crmToken/terms-of-service" component={AgencyTermsOfUsePage} />
          <Route path="/crm" component={CRMApp} />
          <Route path="/submissions" component={PrivateCSApp} />
          <Route path="/cases" component={PrivateCEApp} />
          <Route path={oauthLoginCallbackURL} component={LoginCallback} />
          <Route path={oauthLogoutCallbackURL} component={LogoutCallback} />
          <PrivateRoute path="/agencies/:agencyId" component={Home} />
          <Route path="/payment-portal" component={PaymentPortalApp} />
          <Route path="/private-submission" component={PrivateSubmissionApp} />
        </Switch>
        <Alert />
      </Container>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  productLayout: state.productLayout.productLayout,
  isFetchingProductLayout: state.productLayout.isFetching,
});

const mapDispatchToProps = (dispatch: any) => ({
  loginSuccess: (userProfile: IUserProfile) => dispatch(loginSuccess(userProfile)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
